import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import {
  acceptPortalGuestInvitation,
  PortalApiError,
  resendPortalGuestInvitationSms,
} from '../services/portalApi';

export function GuestInvitationAcceptancePage() {
  const invitationParameters = useMemo(
    () => new URLSearchParams(window.location.hash.slice(1)),
    [],
  );
  const invitationId = invitationParameters.get('invitation') ?? '';
  const email = invitationParameters.get('email') ?? '';
  const token = invitationParameters.get('token') ?? '';
  const [isBusy, setIsBusy] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const isComplete = useMemo(
    () => Boolean(invitationId && email && token),
    [email, invitationId, token],
  );

  async function acceptInvitation(): Promise<void> {
    if (!isComplete || !/^\d{6}$/.test(smsCode)) return;
    setIsBusy(true);
    setMessage(null);
    setIsError(false);
    try {
      await acceptPortalGuestInvitation({ invitationId, email, token, smsCode });
      setIsAccepted(true);
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof PortalApiError
        ? error.message
        : 'The invitation could not be accepted.');
    } finally {
      setIsBusy(false);
    }
  }

  async function resendSms(): Promise<void> {
    if (!isComplete) return;
    setIsBusy(true);
    setMessage(null);
    setIsError(false);
    try {
      await resendPortalGuestInvitationSms({ invitationId, email, token });
      setMessage('A new verification code was sent to your mobile phone.');
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof PortalApiError
        ? error.message
        : 'A new SMS code could not be sent.');
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <section className="guest-acceptance-page">
      <div className="guest-acceptance-card">
        <p className="eyebrow">External participant</p>
        <h1>{isAccepted ? 'Registration completed' : 'Accept invitation'}</h1>
        {isAccepted ? (
          <>
            <p>
              Your invitation has been accepted. You can now sign in with your email address and SMS.
            </p>
            <Link className="primary-button button-link"
              to={`/?email=${encodeURIComponent(email)}`}>Log in</Link>
          </>
        ) : (
          <>
            <p>
              Confirm that you want to join the YWAM Norway calling portal
              as <strong>{email || 'the invited guest'}</strong>.
            </p>
            {!isComplete ? (
              <p className="api-message" role="alert">
                The invitation link is incomplete. Ask the Event Manager for a new invitation.
              </p>
            ) : (
              <form className="guest-acceptance-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void acceptInvitation();
                }}>
                <label>
                  Code from SMS
                  <input required type="text" inputMode="numeric"
                    autoComplete="one-time-code" pattern="[0-9]{6}"
                    maxLength={6} placeholder="000000" value={smsCode}
                    onChange={(event) => setSmsCode(
                      event.target.value.replace(/\D/g, '').slice(0, 6),
                    )} />
                </label>
                <div className="event-form-actions">
                  <button className="primary-button" type="submit"
                    disabled={isBusy || smsCode.length !== 6}>
                    {isBusy ? 'Accepting…' : 'Accept invitation'}
                  </button>
                  <button className="secondary-button" type="button"
                    disabled={isBusy} onClick={() => void resendSms()}>
                    Send a new code
                  </button>
                </div>
              </form>
            )}
          </>
        )}
        {message ? <p className={isError ? 'api-message' : 'success-message'}
          role={isError ? 'alert' : 'status'}>{message}</p> : null}
      </div>
    </section>
  );
}
